import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, ChevronLeft, ChevronRight, Loader2, Send, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Panel } from '@/components/erp/primitives'
import { deleteIncident, type IncidentPatch } from './api'
import { useVocabulary } from './useIncident'
import { SKIPPABLE_STEPS, TOTAL_STEPS, useReportDraft } from './wizard/useReportDraft'
import { StepClassification } from './wizard/StepClassification'
import { StepNarrative } from './wizard/StepNarrative'
import { StepLocation } from './wizard/StepLocation'
import { StepImpact } from './wizard/StepImpact'
import { StepAttribution } from './wizard/StepAttribution'
import { StepEvidence } from './wizard/StepEvidence'
import { StepReview } from './wizard/StepReview'

/**
 * The seven-step report.
 *
 * ## Why seven steps and not one form
 *
 * PollSphere's incident module is one 1,508-line component with a flat form of
 * twenty-eight inputs. A field reporter on a phone cannot fill that, and the ones who try
 * abandon it. Seven short steps, three of them explicitly skippable, means a report can be
 * filed in under a minute and enriched later by somebody at a desk.
 *
 * ## The rail
 *
 * Numbered, current highlighted, completed ticked, skippable steps marked as such. Click
 * to jump BACK freely; jumping forward past a step that has not been answered is not
 * offered, because it produces a review screen with holes in it and no explanation.
 *
 * ## Saving
 *
 * Step 1's Next creates the DRAFT row. Every step after that PATCHes on Next. There is no
 * separate save button and no unsaved state to lose — closing the tab at step 4 leaves a
 * draft the register offers to resume.
 */
export function ReportWizard() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const resumeId = params.get('draft')

  const { vocab } = useVocabulary()
  const draft = useReportDraft(resumeId)
  const { incident, step, setStep, saving, loading, error, setError, fieldErrors } = draft

  /*
     The step's own edits, held locally between saves.

     Not because local state is preferable — it is not, and the whole design of this wizard
     says so — but because a PATCH per keystroke is a PATCH per keystroke. The buffer is
     flushed on Next and on Submit, and seeded from the server row whenever that changes,
     so the server stays the source of truth and this never diverges for longer than one
     step.
  */
  const [buffer, setBuffer] = useState<IncidentPatch>({})
  useEffect(() => { setBuffer({}) }, [incident?.id, step])

  const field = <K extends keyof IncidentPatch>(key: K): IncidentPatch[K] =>
    (key in buffer ? buffer[key] : (incident as never)?.[key as never]) as IncidentPatch[K]

  const edit = useCallback((patch: IncidentPatch) => {
    setBuffer((b) => ({ ...b, ...patch }))
    setError(null)
  }, [setError])

  /** Flush the buffer, then move. A refused save leaves the reporter on the step. */
  const advance = async (to: number) => {
    if (Object.keys(buffer).length > 0 || !incident) {
      const saved = await draft.save(buffer)
      if (!saved) return
      // A brand-new draft gets its id into the URL, so a reload resumes rather than
      // starting a second draft for the same event.
      if (!incident) setParams({ draft: saved.id }, { replace: true })
    }
    setStep(Math.max(1, Math.min(TOTAL_STEPS, to)))
  }

  const canLeaveStepOne = Boolean(
    (field('categoryCode') ?? incident?.categoryCode) && (field('title') ?? incident?.title))

  if (loading) return <LoadingBlock label="Opening the draft…" />

  return (
    <div className="space-y-4">
      <StepRail
        step={step}
        furthest={incident ? TOTAL_STEPS : 1}
        onJump={(to) => { if (to < step) void advance(to) }}
      />

      <Panel
        title={STEP_TITLES[step]}
        description={STEP_HINTS[step]}
        right={incident && (
          <span className="font-mono text-erp-xs text-muted-foreground">{incident.ref}</span>
        )}
      >
        <ErrorStrip message={error} className="rounded-md border mb-3" />

        {step === 1 && (
          <StepClassification
            vocab={vocab}
            errors={fieldErrors}
            value={{
              categoryCode: (field('categoryCode') as string | null) ?? null,
              subcategory: (field('subcategory') as string | null) ?? null,
              title: (field('title') as string | null) ?? '',
              occurredAt: (field('occurredAt') as string | null) ?? null,
              occurredAtPrecisionCode: (field('occurredAtPrecisionCode') as string | null) ?? null,
            }}
            onChange={edit}
          />
        )}

        {step === 2 && (
          <StepNarrative
            vocab={vocab}
            errors={fieldErrors}
            value={{
              description: (field('description') as string | null) ?? null,
              severityCode: (field('severityCode') as string | null) ?? null,
              priorityCode: (field('priorityCode') as string | null) ?? null,
            }}
            onChange={edit}
          />
        )}

        {step === 3 && (
          <StepLocation
            errors={fieldErrors}
            value={{
              unitId: (field('unitId') as string | null) ?? null,
              unitName: incident?.unitName ?? null,
              latitude: (field('latitude') as number | null) ?? null,
              longitude: (field('longitude') as number | null) ?? null,
              locationSourceCode: (field('locationSourceCode') as string | null) ?? null,
              locationAccuracyM: (field('locationAccuracyM') as number | null) ?? null,
              locationLabel: (field('locationLabel') as string | null) ?? null,
              locationAbsentReason: (field('locationAbsentReason') as string | null) ?? null,
            }}
            onChange={edit}
          />
        )}

        {step === 4 && (
          <StepImpact
            errors={fieldErrors}
            value={{
              victimCount: (field('victimCount') as number | null) ?? null,
              injuryCount: (field('injuryCount') as number | null) ?? null,
              deathCount: (field('deathCount') as number | null) ?? null,
              arrestsMade: (field('arrestsMade') as number | null) ?? null,
              estimatedVoterImpact: (field('estimatedVoterImpact') as number | null) ?? null,
            }}
            onChange={edit}
          />
        )}

        {step === 5 && (
          <StepAttribution
            vocab={vocab}
            errors={fieldErrors}
            value={{
              perpetrator: (field('perpetrator') as string | null) ?? null,
              perpetratorPartyId: (field('perpetratorPartyId') as string | null) ?? null,
              relatedCandidateId: (field('relatedCandidateId') as string | null) ?? null,
              relatedPartyId: (field('relatedPartyId') as string | null) ?? null,
              sourceOutlet: (field('sourceOutlet') as string | null) ?? null,
              sourceUrl: (field('sourceUrl') as string | null) ?? null,
              sourcePublishedAt: (field('sourcePublishedAt') as string | null) ?? null,
              mediaCoverageCode: (field('mediaCoverageCode') as string | null) ?? null,
            }}
            onChange={edit}
          />
        )}

        {step === 6 && (incident
          ? <StepEvidence incident={incident} onChanged={draft.refresh} />
          : <Note tone="info">Fill in step 1 first — evidence attaches to the draft, and the draft does not exist yet.</Note>)}

        {step === 7 && (incident
          ? (
            <StepReview
              incident={incident}
              errors={fieldErrors}
              onJump={(to) => void advance(to)}
              value={{
                iebcComplaintRef: (field('iebcComplaintRef') as string | null) ?? null,
                dciCaseRef: (field('dciCaseRef') as string | null) ?? null,
                courtCaseRef: (field('courtCaseRef') as string | null) ?? null,
                ncicRef: (field('ncicRef') as string | null) ?? null,
              }}
              onChange={edit}
            />
          )
          : <Note tone="info">There is nothing to review yet — start at step 1.</Note>)}
      </Panel>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={step === 1 || saving}
          onClick={() => void advance(step - 1)}
          className="erp-btn h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>

        {step < TOTAL_STEPS && (
          <button type="button" disabled={saving || (step === 1 && !canLeaveStepOne)}
            onClick={() => void advance(step + 1)}
            className="erp-btn-primary h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {/*
               Always "Next", never "Skip".

               The button does the same thing on every step — save what is filled in and
               move on — so calling it "Skip" on 4, 5 and 6 described the user's likely
               intent rather than the button's action, and read as though anything typed on
               that step would be thrown away. The rail already marks those steps
               "· optional", which is where that information belongs.
            */}
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}

        {step === TOTAL_STEPS && incident && (
          <button type="button" disabled={saving}
            onClick={async () => {
              // Flush the legal-reference edits before submitting; a refused save must not
              // be followed by a submit that silently drops them.
              if (Object.keys(buffer).length > 0 && !(await draft.save(buffer))) return
              const done = await draft.submit()
              if (done) navigate(`/incidents?open=${done.id}`)
            }}
            className="erp-btn-primary h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Submit the report
          </button>
        )}

        {step === 1 && !canLeaveStepOne && (
          <span className="text-erp-xs text-muted-foreground">
            A category and a title are needed to open the draft.
          </span>
        )}

        <div className="ml-auto">
          {incident && incident.statusCode === 'DRAFT' && (
            <button type="button" disabled={saving}
              onClick={async () => {
                try {
                  await deleteIncident(incident.id)
                  navigate('/incidents')
                } catch (err) { setError(explain(err)) }
              }}
              className="erp-btn h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm text-red-700 border-red-300">
              <Trash2 className="h-3.5 w-3.5" /> Discard this draft
            </button>
          )}
        </div>
      </div>

      {incident && (
        <p className="text-erp-xs text-muted-foreground">
          Saved as you go. Close the tab and the register offers this draft back — it is
          yours alone until you submit it.
        </p>
      )}
    </div>
  )
}

const STEP_TITLES: Record<number, string> = {
  1: '1 · Classification',
  2: '2 · Narrative',
  3: '3 · Location',
  4: '4 · Impact',
  5: '5 · Attribution',
  6: '6 · Evidence',
  7: '7 · Review and submit',
}

const STEP_HINTS: Record<number, string> = {
  1: 'What kind of thing this is, and when it happened.',
  2: 'What happened, in words — and how bad it was.',
  3: 'Where, and how you know where.',
  4: 'Optional. Skip it if nobody has counted yet.',
  5: 'Optional. Everything here is recorded as an allegation.',
  6: 'Optional. Photos, recordings, a live feed, a link.',
  7: 'Everything as the desk will receive it.',
}

function StepRail({
  step, furthest, onJump,
}: {
  step: number
  /** The highest step reachable — 1 until the draft row exists. */
  furthest: number
  onJump: (to: number) => void
}) {
  return (
    <ol className="axis-table-scroll flex items-stretch gap-1 min-w-max">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => {
        const done = n < step
        const current = n === step
        const reachable = n <= furthest && n < step
        return (
          <li key={n}>
            <button
              type="button"
              disabled={!reachable}
              onClick={() => onJump(n)}
              aria-current={current ? 'step' : undefined}
              className={cn(
                'h-9 px-3 inline-flex items-center gap-2 rounded-md border text-erp-xs whitespace-nowrap',
                current && 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium',
                !current && done && 'border-border text-muted-foreground hover:text-foreground',
                !current && !done && 'border-dashed border-border text-muted-foreground/60',
              )}
            >
              {done ? <Check className="h-3 w-3" /> : <span className="tabular-nums">{n}</span>}
              <span>{RAIL_LABELS[n]}</span>
              {SKIPPABLE_STEPS.has(n) && (
                <span className="text-muted-foreground/70">· optional</span>
              )}
            </button>
          </li>
        )
      })}
    </ol>
  )
}

const RAIL_LABELS: Record<number, string> = {
  1: 'Classification', 2: 'Narrative', 3: 'Location', 4: 'Impact',
  5: 'Attribution', 6: 'Evidence', 7: 'Review',
}
